/**
 * Board geometry — the ONE place the pipeline board's measurements live.
 *
 * Two modules have to agree on this and they cannot import each other: the
 * board itself (`features/board/*`, a lazy chunk) and the route-level Suspense
 * fallback (`components/app/RouteSkeletons`, which lives in the eager shell
 * bundle and must not pull a lazy chunk in). Before this file they agreed by
 * transcription and had stopped agreeing: the fallback drew SIX 260px columns
 * on a 12px gutter with 104px cards and 52px heads, while the board draws seven
 * fitted tracks on an 8px gutter with 86px cards and 36px heads. That is the
 * ~300ms of wrong track a reader saw on every navigation to /board, followed by
 * a reflow of the entire screen.
 *
 * So the numbers are declared once, here, and the fit is a pure function both
 * sides call with their own shell width. Nothing on the board may re-derive a
 * width from a literal.
 */

/* ────────────────────────────────────────────────────────────────────────────
   Card
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Card box height, excluding the gap. Skeletons and the virtualiser read it.
 *
 * Every part is on the 4px grid: `p-2` (8+8) + subject 48 + `gap-1` (4) + the
 * one meta row 16, plus the 2px the 1px hairline costs on each edge (the box is
 * `border-box`, so the border eats content height — miss it and the flex column
 * silently shrinks the subject by 2px, which clips a third line that is
 * otherwise whole).
 *
 *   2 + 16 + 48 + 4 + 16 = 86
 *
 * 86, NOT 102, and the 16px came off the footer rather than off the title.
 * The card used to run a second 16px line under the meta row carrying nothing
 * but the account string, while the meta row itself measured 63px of unused
 * slack at a 221px column. At 102 + an 8px gap the 701px scroller held six
 * whole cards — 5.5% of a 766-record pipeline on the default viewport, against
 * the 9–10 Linear and Height fit in a column of the same height. The account is
 * the last cell of the meta row now (truncating, with `contact · company` on
 * its `title`), the footer is gone, and the same scroller holds seven: +17%.
 *
 * WHY THE TITLE DID NOT PAY. Measured over all 175 loaded subjects, 13px type
 * needs three 16px lines to print 175 of them whole; two lines prints 102. The
 * 48px subject took subject clipping from 134/175 to 0/175 and is not for sale.
 */
export const CARD_HEIGHT = 86;

/** Vertical gutter between two cards. */
export const CARD_GAP = 8;

/** One card's slot on the virtualiser's axis. */
export const CARD_SLOT = CARD_HEIGHT + CARD_GAP;

/**
 * Height of the clamped subject block: three 16px line boxes at 13px type.
 *
 * ONE GEOMETRY, and that is the whole reason there is no longer a density map
 * here. `compact` bought its extra card by clamping the subject to two lines,
 * which clipped 72 of 175 titles against 0 of 175 at comfortable — 41% of the
 * board's primary field for one more card. With the footer row gone the two
 * settings land on the same whole-card count anyway (7 either way in a 701px
 * scroller), so the control was offering a choice that no longer had two sides.
 * Linear and Trello ship density on lists and one geometry on a board; so does
 * this. The reader's density preference still governs every list surface
 * through `lib/density`; the board simply is not one.
 */
export const SUBJECT_HEIGHT = 48;

/** Lines the clamp allows, in step with `SUBJECT_HEIGHT` and the line box. */
export const SUBJECT_LINES = 3;

/**
 * Padding inside a column body, and inside a card. One number, because the
 * ghost, the skeleton, the empty column and the loaded column all have to sit
 * on the same inset or they drift apart by 2px each time someone edits one of
 * them. 8, not 10: 10 is not on the 4px grid the rest of the geometry keeps.
 */
export const COLUMN_INSET = 8;
/** Tailwind spelling of the same number, for the class strings. */
export const COLUMN_INSET_CLASS = 'p-2';

/** The head's box. `BoardCardSkeleton` stands in for exactly this. */
export const COLUMN_HEADER_HEIGHT = 36;

/* ────────────────────────────────────────────────────────────────────────────
   Track
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * The pre-measurement fallback, and the width the drag ghost falls back to if
 * it is ever rendered outside the board.
 */
export const COLUMN_WIDTH = 260;
/**
 * 216, not 248. At 248 the fitter got four whole tracks out of the 1184px the
 * board has at 1440×900, so Proposal, Won and Lost were permanently off the
 * default view and reachable only through the rail. At 216 the fitter yields
 * five tracks at 1440 and six at 1920, and the card body still holds a
 * reference, a chip, the account and the score.
 */
export const COLUMN_WIDTH_MIN = 216;
export const COLUMN_WIDTH_MAX = 320;

/** The custom property the track publishes and every width consumer reads. */
export const COLUMN_WIDTH_VAR = '--af-board-column';

/** `var(--af-board-column, 260px)` — use this, never the number. */
export const columnWidth = `var(${COLUMN_WIDTH_VAR}, ${COLUMN_WIDTH}px)`;

/** The track's own inset, matching the toolbar's gutters. */
export const TRACK_INSET = 16;

/** The lane the off-screen rail owns. Reserved, never floated over a column. */
export const RAIL_WIDTH = 52;

/** How many columns the board draws before it has data — the canonical pipeline. */
export const SKELETON_COLUMNS = 7;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * How many whole columns fit in `available`, and how wide each of them is.
 *
 * A fixed 260px track sliced the fifth column through its letterforms at 1440.
 * The track divides the width it actually has into a whole number of columns,
 * so a column is either fully readable or fully on the rail, never in between.
 */
export function fitColumns(
  available: number,
  count: number,
): { width: number; visible: number } {
  const slot = COLUMN_WIDTH_MIN + CARD_GAP;
  const visible = clamp(Math.floor((available + CARD_GAP) / slot), 1, Math.max(1, count));
  const width = clamp(
    (available - CARD_GAP * (visible - 1)) / visible,
    COLUMN_WIDTH_MIN,
    COLUMN_WIDTH_MAX,
  );
  return { width: Math.floor(width), visible };
}

/**
 * The whole fit for a shell of `shellWidth`, rail included.
 *
 * The rail lane is decided by the column count, not by a previous measurement,
 * so reserving it can never feed back into the width that decides whether it is
 * needed. When it is needed it replaces the track's trailing inset rather than
 * sitting on top of it: the last readable column ends where the rail begins.
 */
export function fitTrack(
  shellWidth: number,
  count: number,
): { width: number; visible: number; rail: boolean } {
  const columns = count || 1;
  const raw = shellWidth - TRACK_INSET * 2;
  const first = fitColumns(raw, columns);
  const rail = columns > first.visible;
  const available = rail ? shellWidth - TRACK_INSET - RAIL_WIDTH : raw;
  const fitted = fitColumns(available, columns);
  return { width: fitted.width, visible: fitted.visible, rail };
}

/**
 * The width to paint on the FIRST frame, before any element has been measured.
 *
 * `useState(COLUMN_WIDTH_MIN)` published a width the track was never going to
 * keep, and the `useEffect` that corrected it ran after the browser had already
 * painted. The board therefore laid out once wrong and once right on every
 * navigation. This reads the shell region synchronously during render, so the
 * seed is the real answer on every viewport the app actually mounts in; the
 * layout effect that follows still measures the true box before paint, and the
 * track stays `visibility: hidden` until it has.
 */
export function seedColumnWidth(count: number): number {
  if (typeof document === 'undefined') return COLUMN_WIDTH_MIN;
  const main = document.getElementById('af-main');
  const shell =
    main?.clientWidth || (typeof window === 'undefined' ? 0 : window.innerWidth);
  if (!shell) return COLUMN_WIDTH_MIN;
  return fitTrack(shell, count).width;
}
